import React from 'react';
import { Button } from '../../../../../components/ui/Button/Button';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { FormSection, SectionTitle } from '../../styles/wizardStyles';
import { useDocuments } from '../../hooks/useDocuments';
import { isConditionalDocumentRequired } from '../../rules/documentRules';
import { RequerenteInfoBox, LimitesInfoBox, ReservistaInfoMessage } from './DocumentInfoBox';
import DocumentProgress from './DocumentProgress';
import DocumentSection from './DocumentSection';
import AutoGeneratedDocumentItem from './AutoGeneratedDocumentItem';
import DocumentosAdicionais from './DocumentosAdicionais';
import { ResumoPendencias } from '../../../../../components/ui/ResumoPendencias/ResumoPendencias';
import { ButtonGroup, DocumentSection as DocumentSectionWrapper, DocumentSectionHeader, DocumentList } from './styles';

const DocumentStep = ({
  formData,
  loading: externalLoading,
  onComplete,
  onBack,
  config,
  pedidoName = "Pedido",
  nomeRequerente,
  documentoRequerente,
  numeroPedido,
  comTaxa
}) => {
  const {
    documentos,
    requiredDocs,
    visibleConditionalDocs,
    optionalDocs,
    progress,
    loading: internalLoading,
    documentosFaltando,
    uploadErros,
    tentouAvancar,
    setTentouAvancar,
    resumoDoc,
    resumoFile,
    resumoGerado,
    permiteDocumentosAdicionais,
    documentosAdicionais,
    erroAdicionais,
    handleAdicionarAdicionais,
    handleLimparAdicionais,
    handleUpload,
    handleRemove,
    handleSubmit,
  } = useDocuments(config, formData, onComplete, { nomeRequerente, numeroPedido, comTaxa });

  const loading = externalLoading || internalLoading;

  // Só destaca os itens depois que a pessoa tentou continuar.
  const pendentes = tentouAvancar ? new Set(documentosFaltando.map(doc => doc.id)) : null;

  // Processa documentos condicionais para incluir a flag de required dinâmica
  const processedConditionalDocs = visibleConditionalDocs.map(doc => ({
    ...doc,
    required: isConditionalDocumentRequired(doc, formData),
    conditional: true
  }));

  return (
    <FormSection>
      <SectionTitle>Documentos para {pedidoName}</SectionTitle>

      <RequerenteInfoBox formData={formData} nomeRequerente={nomeRequerente} documentoRequerente={documentoRequerente} />
      <LimitesInfoBox />
      <ReservistaInfoMessage formData={formData} />

      {progress.total > 0 && (
        <DocumentProgress sent={progress.sent} total={progress.total} />
      )}



      <DocumentSectionWrapper>
        <DocumentSectionHeader>Resumo do Requerimento</DocumentSectionHeader>
        <DocumentList>
          <AutoGeneratedDocumentItem documento={resumoDoc} file={resumoFile} pronto={resumoGerado} />
        </DocumentList>
      </DocumentSectionWrapper>

      <DocumentSection
        type="required"
        documents={requiredDocs}
        documentos={documentos}
        onUpload={handleUpload}
        onRemove={handleRemove}
        disabled={loading}
        loading={loading}
        uploadErros={uploadErros}
        pendentes={pendentes}
      />

      <DocumentSection
        type="conditional"
        documents={processedConditionalDocs}
        documentos={documentos}
        onUpload={handleUpload}
        onRemove={handleRemove}
        disabled={loading}
        loading={loading}
        uploadErros={uploadErros}
        pendentes={pendentes}
        subtitle="(Podem ser obrigatórios dependendo da sua situação)"
      />

      <DocumentSection
        type="optional"
        documents={optionalDocs}
        documentos={documentos}
        onUpload={handleUpload}
        onRemove={handleRemove}
        disabled={loading}
        loading={loading}
        uploadErros={uploadErros}
        pendentes={pendentes}
      />

      {permiteDocumentosAdicionais && (
        <DocumentosAdicionais
          arquivos={documentosAdicionais}
          erro={erroAdicionais}
          onAdicionar={handleAdicionarAdicionais}
          onLimpar={handleLimparAdicionais}
          onRemove={handleRemove}
          disabled={loading}
        />
      )}

      {tentouAvancar && (
        <ResumoPendencias
          singular="documento"
          plural="documentos"
          itens={documentosFaltando.map(doc => ({ id: doc.id, label: doc.name }))}
          onClose={() => setTentouAvancar(false)}
        />
      )}

      <ButtonGroup>
        <Button variant="secondary" onClick={onBack} disabled={loading} leftIcon={<FaArrowLeft />}>
          Voltar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit} 
          disabled={loading || !resumoGerado}
          leftIcon={<FaCheck />}
        >
          {loading
            ? 'Enviando documentos...'
            : !resumoGerado
              ? 'Gerando resumo...'
              : progress.total > 0
                ? `Continuar (${progress.sent}/${progress.total})`
                : 'Continuar'}
        </Button>
      </ButtonGroup>
    </FormSection>
  );
};

export default DocumentStep;